import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import CustomButton from 'Components/CustomButton/CustomButton'
import CreateMessage from 'containers/MyInbox/CreateMessage'

// Catalogue de recettes partagé entre utilisateurs Life :
// - parcourir les recettes publiées (qui / quand),
// - cliquer sur le nom du publieur → message Life pré-adressé,
// - copier une recette dans « Mes recettes ».
export const RecetteCatalog = () => {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [catalog, setCatalog] = useState({ results: [], count: 0, next: null })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState(null) // détail chargé (dialog)
  const [detailLoading, setDetailLoading] = useState(false)
  const [messageTo, setMessageTo] = useState(false) // { recipientUser, title }
  const [snack, setSnack] = useState(null) // { severity, text, recetteId }

  useEffect(() => {
    fetchCatalog()
  }, [])

  const buildUrl = (offset = 0, nom = search) =>
    `${urls.recetteCatalog}?limit=20&offset=${offset}${nom ? `&Nom=${encodeURIComponent(nom)}` : ''}`

  const fetchCatalog = async (nom = search) => {
    setLoading(true)
    const response = await makeGetRequest(buildUrl(0, nom), {}, true)
    if (response.status === 200) setCatalog(response.data)
    setLoading(false)
  }

  const fetchMore = async () => {
    setLoading(true)
    const response = await makeGetRequest(buildUrl(catalog.results.length), {}, true)
    if (response.status === 200)
      setCatalog({ ...response.data, results: [...catalog.results, ...response.data.results] })
    setLoading(false)
  }

  const openDetail = async (recette) => {
    setDetailLoading(true)
    setDetail({ ...recette, ingredients: [], instructions: [] })
    const response = await makeGetRequest(`${urls.recetteCatalog}${recette.id}/`, {}, true)
    if (response.status === 200) setDetail(response.data)
    setDetailLoading(false)
  }

  const addToMyRecipes = async (recette) => {
    // makePostRequest ne throw pas : il renvoie error.response en cas d'échec
    const response = await makePostRequest(`${urls.recetteCatalog}${recette.id}/add/`, {}, {}, true)
    if (response?.status === 201) {
      setCatalog((prev) => ({
        ...prev,
        results: prev.results.map((r) => (r.id === recette.id ? { ...r, already_added: true } : r)),
      }))
      if (detail && detail.id === recette.id) setDetail({ ...detail, already_added: true })
      setSnack({
        severity: 'success',
        text: t('recipes.catalog.addSuccess'),
        recetteId: response.data.recette_id,
      })
    } else if (response?.data?.code === 'already_added') {
      setSnack({ severity: 'info', text: response.data.detail, recetteId: response.data.recette_id })
    } else {
      setSnack({
        severity: 'error',
        text: response?.data?.detail || t('recipes.catalog.addError'),
      })
    }
  }

  const publisherName = (recette) =>
    recette.publisher ? `${recette.publisher.prenom || ''} ${recette.publisher.nom || ''}`.trim() : ''

  const openMessage = (recette) => {
    if (!recette.publisher || recette.is_mine) return
    setMessageTo({
      recipientUser: { id: recette.publisher.id, label: publisherName(recette) },
      title: t('recipes.catalog.msgSubject', { name: recette.Nom }),
    })
  }

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '')

  const totalTime = (r) => `${r.TempsTotalH || 0} H ${r.TempsTotalMin || 0} min`

  const PublisherLine = ({ recette }) => (
    <Typography variant="body2" sx={{ color: '#adadad' }}>
      {t('recipes.catalog.publishedBy')}{' '}
      {recette.is_mine ? (
        <Box component="span" fontWeight={600} color="#fff">
          {t('recipes.catalog.you')}
        </Box>
      ) : (
        <Link
          component="button"
          type="button"
          onClick={() => openMessage(recette)}
          sx={{ color: '#7cc0ff', fontWeight: 600, verticalAlign: 'baseline' }}
          title={t('recipes.catalog.sendMessage', { name: publisherName(recette) })}
        >
          {publisherName(recette) || 'Life'}
        </Link>
      )}{' '}
      {t('recipes.catalog.publishedOn', { date: formatDate(recette.published_at) })}
    </Typography>
  )

  const ActionButtons = ({ recette, inDialog = false }) => (
    <Box display="flex" gap={1} flexWrap="wrap">
      {!inDialog && (
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          sx={{ color: '#7cc0ff', textTransform: 'none' }}
          onClick={() => openDetail(recette)}
        >
          {t('recipes.catalog.view')}
        </Button>
      )}
      {recette.is_mine ? (
        <Chip size="small" label={t('recipes.catalog.myRecipe')} sx={{ color: '#fff', bgcolor: '#33507a' }} />
      ) : recette.already_added ? (
        <Button size="small" disabled startIcon={<CheckCircleIcon />} sx={{ textTransform: 'none' }}>
          {t('recipes.catalog.alreadyAdded')}
        </Button>
      ) : (
        <Button
          size="small"
          startIcon={<AddCircleIcon />}
          sx={{ color: '#8be28b', textTransform: 'none' }}
          onClick={() => addToMyRecipes(recette)}
        >
          {t('recipes.catalog.addToMine')}
        </Button>
      )}
    </Box>
  )

  return (
    <>
      {messageTo && (
        <CreateMessage
          setMOpen={() => setMessageTo(false)}
          title={messageTo.title}
          recipientUser={messageTo.recipientUser}
        />
      )}

      <Box bgcolor={'#001b42'} p={1}>
        <Container maxWidth="xl">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <IconButton onClick={() => navigate('/recette')} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" color="white" fontWeight={600}>
              {t('recipes.catalog.title')} ({catalog.count})
            </Typography>
            <Box flexGrow={1} />
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchCatalog()}
              placeholder={t('recipes.catalog.searchName')}
              sx={{
                bgcolor: 'white',
                borderRadius: 1,
                width: isMobile ? '100%' : 280,
                '.MuiInputBase-input': { boxShadow: 'unset' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => fetchCatalog()}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#adadad', ml: isMobile ? 0 : 6 }}>
            {t('recipes.catalog.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 2 }}>
        {loading && catalog.results.length === 0 && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && catalog.results.length === 0 && (
          <Box textAlign="center" py={6} color="#adadad">
            <RestaurantIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography>{t('recipes.catalog.empty')}</Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {catalog.results.map((recette) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={recette.id}>
              <Box
                sx={{
                  bgcolor: '#0b2a5b',
                  border: '1px solid #1d3f79',
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    height: 140,
                    background: recette.Image
                      ? `url(${recette.Image}) center/cover no-repeat`
                      : 'linear-gradient(135deg,#123963,#0b2a5b)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => openDetail(recette)}
                >
                  {!recette.Image && <RestaurantIcon sx={{ fontSize: 48, color: '#33507a' }} />}
                </Box>
                <Box p={1.5} display="flex" flexDirection="column" gap={0.75} flexGrow={1}>
                  <Typography
                    fontWeight={700}
                    color="white"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(recette)}
                  >
                    {recette.Nom}
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {recette.TypeDeRepas && (
                      <Chip size="small" label={recette.TypeDeRepas} sx={{ color: '#fff', bgcolor: '#1d3f79' }} />
                    )}
                    {recette.typeDePlat?.Name && (
                      <Chip size="small" label={recette.typeDePlat.Name} sx={{ color: '#fff', bgcolor: '#1d3f79' }} />
                    )}
                    {recette.Season && (
                      <Chip size="small" label={recette.Season} sx={{ color: '#fff', bgcolor: '#1d3f79' }} />
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5} color="#adadad" fontSize={13}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AccessTimeIcon sx={{ fontSize: 16 }} /> {totalTime(recette)}
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <RestaurantIcon sx={{ fontSize: 16 }} /> {recette.Servir || 0} pers.
                    </Box>
                  </Box>
                  <PublisherLine recette={recette} />
                  <Box mt="auto">
                    <ActionButtons recette={recette} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {catalog.next && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CustomButton onClick={fetchMore} disabled={loading}>
              {t('recipes.catalog.loadMore')}
            </CustomButton>
          </Box>
        )}
      </Container>

      {/* Dialog détail : fiche + ingrédients + instructions */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{detail.Nom}</DialogTitle>
            <DialogContent dividers>
              <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2}>
                {detail.Image && (
                  <Box
                    component="img"
                    src={detail.Image}
                    alt={detail.Nom}
                    sx={{ width: isMobile ? '100%' : 260, borderRadius: 1, objectFit: 'cover' }}
                  />
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {[detail.TypeDeRepas, detail.typeDePlat?.Name, detail.typeDeCuisine?.Name, detail.Season]
                      .filter(Boolean)
                      .join(' • ')}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <AccessTimeIcon sx={{ fontSize: 15, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {totalTime(detail)} — {detail.Servir || 0} pers.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    {detail.Description}
                  </Typography>
                  <Box mt={1} sx={{ '& .MuiTypography-root': { color: '#555' }, '& a': { color: '#1565c0' } }}>
                    <PublisherLine recette={detail} />
                  </Box>
                </Box>
              </Box>

              {detailLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3} mt={2}>
                  <Box width={isMobile ? '100%' : '45%'}>
                    <Typography fontWeight={700} gutterBottom>
                      {t('recipes.catalog.ingredients')}
                    </Typography>
                    {(detail.ingredients || []).map((ing) => (
                      <Typography key={ing.id} variant="body2">
                        • {ing.Quantite || ''} {ing.um?.UM || ''} {ing.NomIngredient}
                      </Typography>
                    ))}
                  </Box>
                  <Box width={isMobile ? '100%' : '55%'}>
                    <Typography fontWeight={700} gutterBottom>
                      {t('recipes.catalog.instructions')}
                    </Typography>
                    {(detail.instructions || []).map((ins) => (
                      <Typography key={ins.id} variant="body2" gutterBottom>
                        {ins.Numero}. {ins.Description}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <ActionButtons recette={detail} inDialog />
              <Button onClick={() => setDetail(null)}>{t('recipes.catalog.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            action={
              snack.recetteId ? (
                <Button color="inherit" size="small" onClick={() => navigate(`/recette/${snack.recetteId}`)}>
                  {t('recipes.catalog.view')}
                </Button>
              ) : null
            }
          >
            {snack.text}
          </Alert>
        ) : null}
      </Snackbar>
    </>
  )
}

export default RecetteCatalog
